import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Polls /version.json to detect new builds.
 * When a version mismatch is detected:
 * 1. Shows a toast notification with "Reload Now" button
 * 2. Auto-reloads on the next route change
 * 3. Force-reloads after 2 minutes if user doesn't navigate
 *
 * Prevents "Failed to fetch dynamically imported module" errors
 * caused by stale chunk hashes after deployments.
 */
const VERSION_CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes
const FORCE_RELOAD_DELAY = 2 * 60 * 1000; // 2 minutes after detection

export function useVersionCheck() {
  const currentVersion = useRef<string | null>(null);
  const needsReload = useRef(false);
  const forceReloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastShown = useRef(false);
  const location = useLocation();

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;

      // Guard against SPA fallback returning HTML instead of JSON
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return;

      const data = await res.json();
      const serverVersion = data?.version;
      if (!serverVersion) return;

      if (currentVersion.current === null) {
        currentVersion.current = serverVersion;
        return;
      }

      if (serverVersion !== currentVersion.current && !needsReload.current) {
        console.log(
          `[VersionCheck] New build detected: ${currentVersion.current} → ${serverVersion}`
        );
        needsReload.current = true;

        // Show toast notification (only once)
        if (!toastShown.current) {
          toastShown.current = true;
          toast('Mise à jour disponible', {
            description: 'Une nouvelle version est disponible. Rechargez pour mettre à jour.',
            duration: Infinity,
            action: {
              label: 'Recharger',
              onClick: () => window.location.reload(),
            },
          });
        }

        // Force reload after timeout if user doesn't navigate
        if (!forceReloadTimer.current) {
          forceReloadTimer.current = setTimeout(() => {
            if (needsReload.current) {
              console.log('[VersionCheck] Force reloading after timeout.');
              window.location.reload();
            }
          }, FORCE_RELOAD_DELAY);
        }
      }
    } catch {
      // Network error — ignore silently
    }
  }, []);

  // Start polling
  useEffect(() => {
    const initialTimer = setTimeout(checkVersion, 5000);
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      if (forceReloadTimer.current) {
        clearTimeout(forceReloadTimer.current);
      }
    };
  }, [checkVersion]);

  // Reload on route change if a new version was detected
  useEffect(() => {
    if (needsReload.current) {
      console.log('[VersionCheck] Reloading on navigation to pick up new build.');
      window.location.reload();
    }
  }, [location.pathname]);
}
